// src/components/users/UserProfile.jsx
import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { FormInput, FormSelect } from '../forms';
import {
  User, Mail, Phone, Book, Calendar, Users, Edit2, Shield,
  UserCircle, Save, X, GraduationCap, Heart, Key, Lock
} from 'lucide-react';

export const UserProfile = ({ userId }) => {
  const { get, put } = useApi();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Password change state
  const { changePassword } = useAuth();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await get(userId ? `/users/${userId}` : '/auth/me');
        setUser(response.data.data.user);
        setFormData(response.data.data.user);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await put('/auth/profile', formData);
      setUser(response.data.data.user);
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      setErrors(error.response?.data?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setPasswordError('New passwords do not match');
    }

    if (passwordData.newPassword.length < 6) {
      return setPasswordError('Password must be at least 6 characters');
    }

    setChangingPwd(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword, passwordData.confirmPassword);
      setPasswordSuccess('Password updated successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setIsChangingPassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password');
    } finally {
      setChangingPwd(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
      </div>
    );
  }

  const canEdit = currentUser.id === user.id ||
    ["admin", "receptionist", "counselor", "hod"].includes(currentUser.role);

  const getInitials = (name) => {
    return name?.split(' ').map(part => part[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
      counselor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      student: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      alumni: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
      hod: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
      receptionist: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border-teal-200 dark:border-teal-800',
      staff: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700',
      faculty: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
    };
    return colors[role] || colors.staff;
  };

  // Reusable info display component
  const InfoItem = ({ icon: Icon, label, value, iconBg = "bg-blue-50 dark:bg-blue-900/20" }) => (
    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-slate-700/30 rounded-xl">
      <div className={`p-2.5 ${iconBg} rounded-lg shrink-0`}>
        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-base font-semibold text-gray-900 dark:text-white mt-0.5 break-words">
          {value || <span className="text-gray-400 dark:text-gray-500 italic">Not provided</span>}
        </p>
      </div>
    </div>
  );

  // Section header component
  const SectionHeader = ({ icon: Icon, title, iconColor = "text-blue-600 dark:text-blue-400" }) => (
    <div className="flex items-center gap-3 mb-5">
      <div className="p-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-120px)] pb-8">
      {/* Main Profile Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

        {/* Hero Banner */}
        <div className="relative h-40 md:h-48 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyem0tNiA2aC00djJoNHYtMnptMC02aC00djJoNHYtMnptMC02aC00djJoNHYtMnptNiAwaC0ydjJoMnYtMnptNiAwdi0yaC0ydjJoMnptLTYtNnYtMmgtMnYyaDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

          {/* Edit Button - Top Right */}
          {canEdit && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-slate-700 transition-all"
            >
              <Edit2 className="h-4 w-4" />
              <span className="hidden sm:inline">Edit Profile</span>
            </button>
          )}
        </div>

        {/* Profile Header */}
        <div className="relative px-6 md:px-8 pb-6">
          {/* Avatar */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-16 sm:-mt-12">
            <div className="relative">
              <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-2xl border-4 border-white dark:border-slate-800 bg-gradient-to-br from-blue-500 to-indigo-600 shadow-xl flex items-center justify-center text-white text-4xl sm:text-5xl font-bold">
                {getInitials(user.fullName)}
              </div>
              <div className="absolute -bottom-1 -right-1 h-6 w-6 bg-green-500 border-3 border-white dark:border-slate-800 rounded-full ring-2 ring-green-500/30" />
            </div>

            <div className="flex-1 sm:pb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                {user.fullName}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(user.role)}`}>
                  <Shield className="h-3 w-3" />
                  <span className="capitalize">{user.role}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                  <UserCircle className="h-3 w-3" />
                  @{user.username}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="px-6 md:px-8 pb-8">
          {isEditing ? (
            /* ==================== EDIT MODE ==================== */
            <form onSubmit={handleSubmit} className="space-y-8 pt-4 border-t border-gray-100 dark:border-gray-700">

              {/* Personal Info Section */}
              <div>
                <SectionHeader icon={User} title="Personal Information" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormInput
                    label="Full Name"
                    value={formData.fullName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    error={errors.fullName}
                    required
                  />
                  <FormInput
                    label="Username"
                    value={formData.username || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    error={errors.username}
                    required
                  />
                  <FormInput
                    label="Email Address"
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    error={errors.email}
                    required
                  />
                  <FormInput
                    label="Phone Number"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    error={errors.phone}
                  />
                </div>
              </div>

              {/* Academic Info (Students) */}
              {['student', 'alumni'].includes(user.role) && (
                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                  <SectionHeader icon={GraduationCap} title="Academic Information" iconColor="text-purple-600 dark:text-purple-400" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInput
                      label="Major / Program"
                      value={formData.profile?.major || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        profile: { ...prev.profile, major: e.target.value }
                      }))}
                      error={errors.profile?.major}
                    />
                    <FormSelect
                      label="Year of Study"
                      value={formData.profile?.yearOfStudy || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        profile: { ...prev.profile, yearOfStudy: e.target.value }
                      }))}
                      options={[
                        { value: '1', label: 'Year 1 - Freshman' },
                        { value: '2', label: 'Year 2 - Sophomore' },
                        { value: '3', label: 'Year 3 - Junior' },
                        { value: '4', label: 'Year 4 - Senior' },
                        { value: '5', label: 'Graduate Student' },
                      ]}
                      error={errors.profile?.yearOfStudy}
                    />
                  </div>
                </div>
              )}

              {/* Emergency Contact (Students) */}
              {['student', 'alumni'].includes(user.role) && (
                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                  <SectionHeader icon={Heart} title="Emergency Contact" iconColor="text-rose-600 dark:text-rose-400" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInput
                      label="Contact Name"
                      value={formData.nextOfKin?.name || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nextOfKin: { ...prev.nextOfKin, name: e.target.value }
                      }))}
                      error={errors.nextOfKin?.name}
                      required
                    />
                    <FormInput
                      label="Relationship"
                      value={formData.nextOfKin?.relation || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nextOfKin: { ...prev.nextOfKin, relation: e.target.value }
                      }))}
                      error={errors.nextOfKin?.relation}
                      required
                    />
                    <FormInput
                      label="Phone Number"
                      value={formData.nextOfKin?.phone || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nextOfKin: { ...prev.nextOfKin, phone: e.target.value }
                      }))}
                      error={errors.nextOfKin?.phone}
                      required
                    />
                    <FormInput
                      label="Email Address"
                      type="email"
                      value={formData.nextOfKin?.email || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nextOfKin: { ...prev.nextOfKin, email: e.target.value }
                      }))}
                      error={errors.nextOfKin?.email}
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-colors"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 font-semibold transition-all"
                >
                  {saving ? (
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            /* ==================== VIEW MODE ==================== */
            <div className="space-y-8 pt-4 border-t border-gray-100 dark:border-gray-700">

              {/* Contact Information */}
              <div>
                <SectionHeader icon={Mail} title="Contact Information" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoItem icon={Mail} label="Email" value={user.email} />
                  <InfoItem icon={Phone} label="Phone" value={user.phone} />
                  <InfoItem icon={UserCircle} label="Username" value={`@${user.username}`} />
                </div>
              </div>

              {/* Staff Details (Staff/Faculty) */}
              {['staff', 'faculty', 'counselor', 'hod'].includes(user.role) && user.staffInfo && (
                <div>
                  <SectionHeader icon={Shield} title="Job Details" iconColor="text-teal-600 dark:text-teal-400" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      icon={UserCircle}
                      label="Position"
                      value={user.staffInfo.position}
                      iconBg="bg-teal-50 dark:bg-teal-900/20"
                    />
                    <InfoItem
                      icon={Users}
                      label="Department"
                      value={user.staffInfo.department}
                      iconBg="bg-teal-50 dark:bg-teal-900/20"
                    />
                  </div>
                </div>
              )}

              {/* Academic Details (Students) */}
              {['student', 'alumni'].includes(user.role) && user.profile && (
                <div>
                  <SectionHeader icon={GraduationCap} title="Academic Details" iconColor="text-purple-600 dark:text-purple-400" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      icon={Book}
                      label="Major / Program"
                      value={user.profile.major}
                      iconBg="bg-purple-50 dark:bg-purple-900/20"
                    />
                    <InfoItem
                      icon={Calendar}
                      label="Year of Study"
                      value={user.calculatedYearOfStudy || (user.profile?.yearOfStudy ? `Year ${user.profile.yearOfStudy}` : null)}
                      iconBg="bg-purple-50 dark:bg-purple-900/20"
                    />
                  </div>
                </div>
              )}

              {/* Emergency Contact (Students) */}
              {['student', 'alumni'].includes(user.role) && user.nextOfKin && (
                <div>
                  <SectionHeader icon={Heart} title="Emergency Contact" iconColor="text-rose-600 dark:text-rose-400" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <InfoItem
                      icon={User}
                      label="Contact Name"
                      value={user.nextOfKin.name}
                      iconBg="bg-rose-50 dark:bg-rose-900/20"
                    />
                    <InfoItem
                      icon={Users}
                      label="Relationship"
                      value={user.nextOfKin.relation}
                      iconBg="bg-rose-50 dark:bg-rose-900/20"
                    />
                    <InfoItem
                      icon={Phone}
                      label="Phone"
                      value={user.nextOfKin.phone}
                      iconBg="bg-rose-50 dark:bg-rose-900/20"
                    />
                    <InfoItem
                      icon={Mail}
                      label="Email"
                      value={user.nextOfKin.email}
                      iconBg="bg-rose-50 dark:bg-rose-900/20"
                    />
                  </div>
                </div>
              )}

              {/* Password Settings Section (Only for own profile) */}
              {currentUser?.id === user._id || currentUser?.id === user.id ? (
                <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-5">
                    <SectionHeader icon={Key} title="Security Settings" iconColor="text-indigo-600 dark:text-indigo-400" />
                    {!isChangingPassword && (
                      <button
                        onClick={() => setIsChangingPassword(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg hover:bg-indigo-700 transition"
                      >
                        Change Password
                      </button>
                    )}
                  </div>

                  {isChangingPassword && (
                    <div className="bg-gray-50 dark:bg-slate-700/30 p-6 rounded-xl border border-gray-100 dark:border-gray-700">
                      <form onSubmit={handlePasswordSubmit} className="space-y-4">
                        {passwordError && (
                          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">
                            {passwordError}
                          </div>
                        )}
                        {passwordSuccess && (
                          <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm border border-green-100">
                            {passwordSuccess}
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormInput
                            label="Current Password"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            required
                          />
                          <FormInput
                            label="New Password"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            required
                          />
                          <FormInput
                            label="Confirm New Password"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="flex justify-end gap-3 mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setIsChangingPassword(false);
                              setPasswordError('');
                              setPasswordSuccess('');
                              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            }}
                            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-600 rounded-lg transition"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={changingPwd}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                          >
                            {changingPwd ? (
                              <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <Lock className="h-4 w-4" />
                            )}
                            Update Password
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};